import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clearToken, getToken, setToken } from '../config/api';
import { fetchMe, login as apiLogin } from '../api/creators';

// Auth/session context for the creator portal. Mirrors the admin/partner flow:
// token in localStorage (key `creator_token`), `GET /user/me` for the session.

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await fetchMe();
      setUser(me);
    } catch (e) {
      // Expired/invalid token → drop it (no refresh endpoint exists).
      if (e.status === 401) clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(
    async (email, password) => {
      const { token, user: u } = await apiLogin(email, password);
      setToken(token);
      // Prefer a fresh /me, fall back to the login user.
      try {
        const me = await fetchMe();
        setUser(me);
      } catch {
        setUser(u);
      }
    },
    []
  );

  // Used by the social (Google / Apple) flows: they already have a token +
  // user from the API, so we just persist it and hydrate /me.
  const loginWithToken = useCallback(async (token, fallbackUser) => {
    setToken(token);
    try {
      const me = await fetchMe();
      setUser(me);
    } catch {
      setUser(fallbackUser || null);
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, loginWithToken, logout, refresh: loadMe }),
    [user, loading, login, loginWithToken, logout, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
